<template>
  <!--
    Print-Kampagnen: Mediaplan- und Belegungsübersicht, Inhalt wie bisher.
      Kopf: Projekt · Kampagne · Zeitraum · Status des Mediaplans · 7 KPI-Kacheln
      Mediaplan-Timeline (Kalender wie der Excel-Mediaplan) · Mediaplan-Details (Tabelle)
  -->
  <MarketingPageTemplate
    :title="data.title || 'Print-Kampagnen'"
    :subtitle="data.subtitle || 'Erscheinungstermine, Platzierungen und Mediaplanwerte im Überblick'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade Print-Mediaplan…"
    @retry="reload"
  >
    <template #channel-filters>
      <div class="fpair">
        <span class="flabel">Titel</span>
        <SingleDropdown
          label="Titel"
          :options="titleOptions"
          :model-value="titleId"
          @update:model-value="(value) => setExtra({ title_id: value })"
        />
      </div>
      <div class="fpair">
        <span class="flabel">Schaltungsstatus</span>
        <SingleDropdown
          label="Schaltungsstatus"
          :options="statusOptions"
          :model-value="printStatus"
          @update:model-value="(value) => setExtra({ print_status: value })"
        />
      </div>
    </template>

    <template #context>
      <li><i class="fas fa-clipboard-check" aria-hidden="true" />Status des Mediaplans: <strong>{{ data.mediaplan_status || '–' }}</strong></li>
    </template>

    <!-- Die 7 KPI-Kacheln (3 + 4, keine Einzelkachel am Zeilenende). -->
    <template #kpis>
      <MarketingKpis :cards="pick(['placements', 'titles', 'issues'])" :columns="3" />
      <MarketingKpis :cards="pick(['gross_value', 'net_value', 'internal', 'barter'])" :columns="4" />
    </template>

    <PrintCalendar
      :events="data.timeline || []"
      :title-names="titleNames"
      :placement-types="filterOptions.placement_types || []"
      :placement-descriptions="PRINT_PLACEMENT_DESCRIPTIONS"
    />

    <DashboardSection title="Mediaplan-Details" flush :empty="!tableRows.length" empty-text="Keine Schaltungen im Zeitraum.">
      <TableScroll label="Mediaplan-Details, horizontal scrollbar">
        <table class="jr-list">
          <thead>
            <tr>
              <!-- UX: Projekt/Kampagne ausblenden, sobald gefiltert – der Kopfbereich zeigt sie schon. -->
              <th v-if="!filters.project_id" scope="col">Projekt</th>
              <th v-if="!filters.campaign_id" scope="col">Kampagne</th>
              <th scope="col">Maßnahme</th>
              <th scope="col">Titel</th>
              <th scope="col">Ausgabe</th>
              <th scope="col">Erscheinungstag</th>
              <th scope="col">Format</th>
              <th v-if="showFrequency" scope="col">Frequenz</th>
              <th scope="col">Schaltungstyp</th>
              <th scope="col">Status</th>
              <th scope="col" class="num">Bruttowert</th>
              <th scope="col" class="num">Nettowert</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableRows" :key="row.id">
              <td v-if="!filters.project_id">{{ row.project }}</td>
              <td v-if="!filters.campaign_id">{{ row.campaign }}</td>
              <td>{{ row.measure }}</td>
              <td :title="row.title_short !== row.title ? `Kürzel: ${row.title_short}` : undefined">{{ row.title }}</td>
              <td>{{ row.issue }}</td>
              <td>{{ formatDate(row.publication_date) }}</td>
              <td>{{ row.format }}</td>
              <td v-if="showFrequency">{{ row.frequency || '–' }}</td>
              <td>
                <span class="mb-cell-name"><PlacementTypeIcon :code="row.placement_type" /> {{ row.placement_type_label }}</span>
              </td>
              <td><StatusLozenge :tone="STATUS_TONES[row.status] || 'default'" :label="row.status_label" :strike="row.status === 'storniert'" /></td>
              <td class="num">{{ formatCurrency(row.gross_value) }}</td>
              <td class="num">{{ formatCurrency(row.net_value) }}</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>

    <template #legend>
      <PlacementTypeLegend :items="filterOptions.placement_types || []" :descriptions="PRINT_PLACEMENT_DESCRIPTIONS" />
    </template>
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import SingleDropdown from '@/features/dashboard/components/SingleDropdown.vue';
import StatusLozenge from '@/components/ui/StatusLozenge.vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import PrintCalendar from '../components/PrintCalendar.vue';
import PlacementTypeIcon from '../components/PlacementTypeIcon.vue';
import PlacementTypeLegend from '../components/PlacementTypeLegend.vue';
import TableScroll from '../components/TableScroll.vue';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import { STATUS_TONES } from '../composables/designTokens';
import { columnHasData } from '../composables/tableUtils';
import { formatCurrency, formatDate } from '../composables/formatters';

const props = defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

// UX: Die Standard-Legende spricht von "eigenen Webseiten" – auf der Print-Seite passt das nicht.
const PRINT_PLACEMENT_DESCRIPTIONS = {
  intern: 'Schaltung in eigenen Titeln.',
  konzern: 'Schaltung in Titeln anderer Konzernmarken.',
  gegengeschaeft: 'Schaltung in externen Titeln im Rahmen eines Gegengeschäfts.'
};

const { filters, extraValue, setExtra } = useMarketingFilters();
const titleId = computed(() => extraValue('title_id', ''));
const printStatus = computed(() => extraValue('print_status', ''));
const extraParams = computed(() => ({
  title_id: titleId.value,
  print_status: printStatus.value
}));
const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/print/', extraParams);

const titleOptions = computed(() => [
  { value: '', label: 'Alle Titel' },
  ...(props.filterOptions.print_titles || []).map((item) => ({ value: String(item.id), label: item.name || item.short_name }))
]);
const statusOptions = computed(() => [
  { value: '', label: 'Alle Status' },
  ...(props.filterOptions.print_statuses || []).map((item) => ({ value: item.code, label: item.name }))
]);

const tableRows = computed(() => data.value.table || []);
const showFrequency = computed(() => columnHasData(tableRows.value, 'frequency'));
// Timeline-Einträge und Tabellenzeilen teilen dieselbe id → voller Titelname für die Timeline.
const titleNames = computed(() => Object.fromEntries(tableRows.value.map((row) => [String(row.id), row.title])));

function pick(keys) {
  const kpis = data.value.kpis || [];
  return keys.map((key) => kpis.find((item) => item.key === key)).filter(Boolean);
}
</script>
